import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { retryWhen } from 'rxjs/operators';
import { HttpClient, HttpHeaders, HttpRequest } from '@angular/common/http';
import { retryStrategy, api } from '../../shared/api-loaders';
import { Transaction, TransactionFailedRecord } from '../model/Transaction';
import { CSVTemplate, PhysicianCSVTemplate, ImportUsersData } from '../model/Import-users';

@Injectable({
  providedIn: 'root'
})
export class ImportUsersService {

  private readonly sampleCSVData: CSVTemplate[] = [{
    userName: 'isaac@test.com',
    firstName: 'Isaac',
    middleName: '',
    lastName: 'Brock',
    email: 'isaac@test.com',
    exportedSystemId: 'aj',
    exportedUserId: 'aj',
    passwordType: 'Temporary',
    password: 'Password1!',
    matrixcareCorporationName: 'MatrixCare Support Corporation',
    matrixcareFacilityName: '',
    matrixcareRole: 'CareAssist Caregiver - Corporate',
    matrixcareUserType: 'Corporate',
    matrixcarePrimaryDiscipline: '',
    oktaUserId: null,
    adminUser: '',
    prefix: '',
    title: '',
    regions: '',
    districts: '',
    facilities: '',
    employeeId: 214001,
    lpcRoleIds: 'admin,nurse'
  }];

  private readonly physicianSampleCSVData: PhysicianCSVTemplate[] = [{
    userName: 'dr.smith@test.com',
    prefix: 'Dr.',
    firstName: 'John',
    middleName: 'A',
    lastName: 'Smith',
    title: 'MD',
    email: 'dr.smith@test.com',
    directEmail: 'dr.smith.direct@test.com',
    primaryPhone: '5552345678',
    primaryPhoneExtension: '5678',
    mobilePhone: '5553456789',
    fax: '555-123-4567',
    pager: '555-999-8888',
    employeeId: 214002,
    isPhysicianOrNppa: 'true',
    clinicMedCenter: 'Springfield Medical Center',
    addressLine1: '123 Medical Plaza',
    addressLine2: 'Suite 100',
    city: 'Springfield',
    state: 'IL',
    zip: '62701',
    upin: 'A12345',
    taxonomyCode: '207Q00000X',
    licenseNumber: 'IL123456',
    npi: '1234567890',
    taxIdNumber: '12-3456789',
    medicaidNumber: 'MCD123456',
    medicareNumber: 'MCR987654',
    nadeanNumber: 'NADEAN123',
    stateDeaNumbers: 'IL:AS1234563;CA:BC7654321',
    passwordType: 'Temporary',
    password: 'Password1!',
    matrixcareCorporationName: 'MatrixCare Support Corporation',
    matrixcareRole: 'Physician',
    facilityRoleAssignments: '5:Attending Physician;6:Cardiologist'
  }];

  public get sampleDataForCSV(): CSVTemplate[] {
    return this.sampleCSVData;
  }

  public get physicianSampleDataForCSV(): PhysicianCSVTemplate[] {
    return this.physicianSampleCSVData;
  }

  constructor(private http: HttpClient) {  }

  public getTransactions(): Observable<any[]> {
    return this.http.get<Transaction[]>(api.importUsersTransactions()).pipe(retryWhen(retryStrategy()));
  }

  public getFailedUsers(transactionId: number): Observable<TransactionFailedRecord[]> {
    return this.http.get<TransactionFailedRecord[]>(api.importFailedUsers(transactionId)).pipe(retryWhen(retryStrategy()));
  }

  public uploadUsers(domain: string, file: File): Observable<any> {
    let formData:FormData = new FormData();
    formData.append('importUsersCsv', file);
    formData.append('customerEmailDomain', domain);
    let headers = new HttpHeaders();
    headers.append('Content-Type', 'multipart/form-data');
    headers.append('Accept', 'application/json');
    const req = new HttpRequest('POST', api.uploadImportUsers(), formData, {
     headers: headers
    });
    return this.http.request(req);
  }

  public import(importData: ImportUsersData): Observable<any> {
    let formData:FormData = new FormData();
    formData.append('crmId', importData.crmId.toString());
    // formData.append('envId', importData.envId.toString());
    // formData.append('source', importData.source);
    formData.append('customerEmailDomain', importData.customerEmailDomain);
    formData.append('importUsersCsv', importData.importUsersCSV);
    formData.append('updateExistingUsers', importData.updateExistingUsers.toString());
    // formData.append('environmentName', importData.environmentName.toString());

    let headers = new HttpHeaders();
    headers.append('Content-Type', 'multipart/form-data');
    headers.append('Accept', 'application/json');

    const req = new HttpRequest('POST', api.usersImport(), formData, {
      headers: headers
    });

    return this.http.request(req);
  }

  public downloadImportsTemplate(processId: number): Observable<Blob> {
    return this.http.get(api.downloadImportsTemplate(processId), { responseType: 'blob' }).pipe(retryWhen(retryStrategy()));
  }

  public downloadSampleTemplate(): void {
    const data = this.sampleDataForCSV;
    const replacer = (_key: string, value: any) => (value === null ? '' : value);
    const header = Object.keys(data[0]);
    const csv = data.map((row) =>
      header
        .map((fieldName) => JSON.stringify(row[fieldName], replacer))
        .join(',')
    );
    csv.unshift(header.join(','));
    const csvArray = csv.join('\r\n');

    const a = document.createElement('a');
    const blob = new Blob([csvArray], { type: 'text/csv' });
    const url = globalThis.URL.createObjectURL(blob);

    a.href = url;
    a.download = 'OktaImport_Temporary_Template - Example.csv';
    a.click();
    globalThis.URL.revokeObjectURL(url);
    a.remove();
  }

  public downloadPhysicianSampleTemplate(): void {
    const data = this.physicianSampleDataForCSV;
    const replacer = (_key: string, value: any) => (value === null ? '' : value);
    const header = Object.keys(data[0]);
    const csv = data.map((row) =>
      header
        .map((fieldName) => JSON.stringify(row[fieldName], replacer))
        .join(',')
    );
    // Data rows are still keyed by the camelCase field names above; only the printed header is PascalCase.
    csv.unshift(header.map((fieldName) => this.toPascalCase(fieldName)).join(','));
    const csvArray = csv.join('\r\n');

    const a = document.createElement('a');
    const blob = new Blob([csvArray], { type: 'text/csv' });
    const url = globalThis.URL.createObjectURL(blob);

    a.href = url;
    a.download = 'OktaImport_Physician_Template - Example.csv';
    a.click();
    globalThis.URL.revokeObjectURL(url);
    a.remove();
  }

  private toPascalCase(fieldName: string): string {
    return fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
  }
}
